import React, { useMemo, useState } from "react";
import { CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";

/**
 * Excel-style sortable table for completed/paid balances.
 *
 * Props:
 *   items: array of balance objects
 *   actions: optional render(b) → JSX shown in the actions column
 */

function fmtDate(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`;
  } catch {
    return s;
  }
}

function fmtDateTime(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return s;
  }
}

function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString("el-GR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const COLUMNS = [
  { key: "supplier", label: "ΠΡΟΜΗΘΕΥΤΗΣ", grow: true, minWidth: "180px" },
  { key: "entry_date", label: "ΗΜ. ΕΓΓΡΑΦΗΣ", width: "120px", isDate: true },
  { key: "amount", label: "ΠΟΣΟ", width: "110px", align: "right", isMoney: true },
  { key: "payment_method", label: "ΤΡΟΠΟΣ", width: "160px" },
  { key: "source", label: "ΠΗΓΗ", width: "110px" },
  { key: "created_by", label: "ΑΠΟ", width: "110px" },
  { key: "execution_date", label: "ΗΜ. ΕΚΤΕΛ.", width: "120px", isDate: true },
  { key: "comment", label: "ΣΧΟΛΙΟ", grow: true, wide: true },
  {
    key: "completed_at",
    label: "ΗΜ. ΕΞΟΦΛ.",
    width: "140px",
    isDate: true,
    isDateTime: true,
    defaultSort: "desc",
  },
];

export default function BalancesTable({ items, actions }) {
  const defaultCol = COLUMNS.find((c) => c.defaultSort) || COLUMNS[COLUMNS.length - 1];
  const [sortKey, setSortKey] = useState(defaultCol.key);
  const [sortDir, setSortDir] = useState(defaultCol.defaultSort || "desc");

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sortKey) || defaultCol;
    const dir = sortDir === "asc" ? 1 : -1;
    const arr = [...items];
    arr.sort((a, b) => {
      const av = a?.[col.key];
      const bv = b?.[col.key];
      if (col.isDate) {
        return ((av || "") > (bv || "") ? 1 : (av || "") < (bv || "") ? -1 : 0) * dir;
      }
      if (col.isMoney || typeof av === "number" || typeof bv === "number") {
        const an = parseFloat(av);
        const bn = parseFloat(bv);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return (an - bn) * dir;
      }
      return String(av || "").localeCompare(String(bv || ""), "el", {
        sensitivity: "base",
      }) * dir;
    });
    return arr;
  }, [items, sortKey, sortDir, defaultCol]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <div
      className="rounded-xl border border-black/10 bg-white overflow-x-auto shadow-sm"
      data-testid="balances-table"
    >
      <table className="w-full text-sm font-arial border-collapse">
        <thead className="bg-ink text-white sticky top-0">
          <tr>
            {COLUMNS.map((c) => {
              const active = c.key === sortKey;
              const Icon = !active
                ? CaretUpDown
                : sortDir === "asc"
                ? CaretUp
                : CaretDown;
              return (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  data-testid={`balances-table-header-${c.key}`}
                  style={
                    c.width
                      ? { width: c.width, minWidth: c.width }
                      : c.minWidth
                      ? { minWidth: c.minWidth }
                      : undefined
                  }
                  className={`px-3 py-2.5 select-none cursor-pointer uppercase tracking-wider text-xs font-black border-r border-white/10 last:border-r-0 hover:bg-ink/85 transition-colors ${
                    c.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  <div
                    className={`inline-flex items-center gap-1 ${
                      c.align === "right" ? "justify-end" : ""
                    }`}
                  >
                    <span>{c.label}</span>
                    <Icon
                      size={12}
                      weight={active ? "bold" : "regular"}
                      className={active ? "text-amber-300" : "text-white/40"}
                    />
                  </div>
                </th>
              );
            })}
            {actions && (
              <th className="px-3 py-2.5 text-right uppercase tracking-wider text-xs font-black w-[180px]">
                ΕΝΕΡΓΕΙΕΣ
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td
                colSpan={COLUMNS.length + (actions ? 1 : 0)}
                className="px-3 py-10 text-center italic text-ink/40 text-base"
              >
                ΧΩΡΙΣ ΕΓΓΡΑΦΕΣ
              </td>
            </tr>
          )}
          {sorted.map((b, i) => (
            <tr
              key={b.id}
              data-testid={`balances-table-row-${b.id}`}
              className={`${
                i % 2 === 0 ? "bg-white" : "bg-black/[0.018]"
              } hover:bg-amber-50/60 border-t border-black/5`}
            >
              {COLUMNS.map((c) => {
                let display;
                const raw = b?.[c.key];
                if (c.isDateTime) display = fmtDateTime(raw);
                else if (c.isDate) display = fmtDate(raw);
                else if (c.isMoney) display = `${fmtMoney(raw)} €`;
                else display = raw;
                return (
                  <td
                    key={c.key}
                    style={
                      c.width
                        ? { width: c.width, minWidth: c.width }
                        : c.minWidth
                        ? { minWidth: c.minWidth }
                        : undefined
                    }
                    className={`px-3 py-2 border-r border-black/5 last:border-r-0 ${
                      c.align === "right" ? "text-right tabular-nums font-bold" : "text-left"
                    } ${c.wide ? "max-w-[280px]" : ""}`}
                  >
                    <div
                      className={`truncate ${
                        c.wide ? "whitespace-pre-line break-words line-clamp-2" : ""
                      }`}
                      title={String(display || "")}
                    >
                      {display || <span className="text-ink/25">—</span>}
                    </div>
                  </td>
                );
              })}
              {actions && (
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1.5">{actions(b)}</div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
